import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Bluelagoon } from "@bluelagoon";
import useInjectScript from "../hooks/useInjectScripts";

/**
 * Google Drive Picker component for React
 * Allows users to select files from their Google Drive
 *
 * @param {Object} props - Component props
 * @param {number} props.integrationId - ID of the Google integration to use
 * @param {Function} props.onSelect - Callback when files are selected
 * @param {string} props.buttonText - Text to display on the picker button
 * @param {boolean} props.multiSelect - Whether to allow multiple file selection
 * @param {Array<string>} props.viewIds - Array of view IDs to include
 * @param {string} props.buttonClassName - CSS class for the button
 */
const GoogleDrivePicker = ({
    integrationId,
    onSelect,
    buttonText = "Select from Google Drive",
    multiSelect = false,
    viewIds = ["docs"],
    callbackFunction = () => {
        // Default callback function
    },
    buttonClassName = "px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300",
}) => {
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [clientId, setClientId] = useState(null);
    const [oauthToken, setOauthToken] = useState(null);
    const [userId, setUserId] = useState(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [config, setConfig] = useState(null);
    const [developerKey, setDeveloperKey] = useState(null);
    const [pickerApiLoaded, setpickerApiLoaded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    let picker;

    const defaultScopes = ["https://www.googleapis.com/auth/drive.readonly"];
    const [loaded, error] = useInjectScript(
        "https://apis.google.com/js/api.js"
    );
    const [loadedGsi, errorGsi] = useInjectScript(
        "https://accounts.google.com/gsi/client"
    );

    const onPickerApiLoad = () => {
        setpickerApiLoaded(true);
    };

    const loadApis = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.gapi.load("auth");
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.gapi.load("picker", { callback: onPickerApiLoad });
    };

    // get the apis from googleapis
    useEffect(() => {
        if (loaded && !error && loadedGsi && !errorGsi && !pickerApiLoaded) {
            loadApis();
        }
    }, [loaded, error, loadedGsi, errorGsi, pickerApiLoaded]);

    // Initialize the auth client when API is loaded
    const initAuth = async () => {
        try {
            setIsLoading(true);
            // Fetch client ID from your backend
            const response = await Bluelagoon.call(
                "get",
                "integrations/google/client-id",
                { integrationId },
                {
                    headers: {
                        Authorization: `Bearer ${Bluelagoon.getToken()}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data?.data;

            const { clientId, token, developerKey } = data;

            setClientId(clientId);
            setOauthToken(token);
            setDeveloperKey(developerKey);
            return { clientId, token, developerKey };
        } catch (error) {
            console.error("Error initializing Google Auth client", error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const createPicker = ({
        token,
        appId = "",
        supportDrives = false,
        developerKey,
        viewId = "DOCS",
        disabled,
        multiselect,
        setOrigin,
        showUploadView = false,
        showUploadFolders,
        setParentFolder = "",
        viewMimeTypes,
        customViews,
        locale = "en",
        setIncludeFolders,
        setSelectFolderEnabled,
        disableDefaultView = false,
        callbackFunction,
    }) => {
        if (disabled) return false;

        const view = new window.google.picker.DocsView(
            window.google.picker.ViewId[viewId]
        );
        if (viewMimeTypes) view.setMimeTypes(viewMimeTypes);
        if (setIncludeFolders) view.setIncludeFolders(true);
        if (setSelectFolderEnabled) view.setSelectFolderEnabled(true);

        const uploadView = new window.google.picker.DocsUploadView();
        if (viewMimeTypes) uploadView.setMimeTypes(viewMimeTypes);
        if (showUploadFolders) uploadView.setIncludeFolders(true);
        if (setParentFolder) uploadView.setParent(setParentFolder);
        if (setParentFolder) view.setParent(setParentFolder);

        picker = new window.google.picker.PickerBuilder()
            .setAppId(appId)
            .setOAuthToken(token)
            .setDeveloperKey(developerKey)
            .setLocale(locale)
            .setCallback(callbackFunction);

        if (setOrigin) {
            picker.setOrigin(setOrigin);
        }

        if (!disableDefaultView) {
            picker.addView(view);
        }

        if (customViews) {
            customViews.map((view) => picker.addView(view));
        }

        if (multiselect) {
            picker.enableFeature(
                window.google.picker.Feature.MULTISELECT_ENABLED
            );
        }

        if (showUploadView) picker.addView(uploadView);

        if (supportDrives) {
            picker.enableFeature(window.google.picker.Feature.SUPPORT_DRIVES);
        }

        picker.build().setVisible(true);
        return true;
    };

    const handleOpen = async () => {
        try {
            // Only fetch credentials when the user opens the picker
            const credentials = await initAuth();

            createPicker({
                token: credentials.token,
                appId: credentials.clientId,
                developerKey: credentials.developerKey,
                viewId: "SHEETS",
                disabled: false,
                multiselect: multiSelect,
                setOrigin: window.location.origin,
                showUploadView: false,
                callbackFunction: callbackFunction,
            });
        } catch (error) {
            console.error("Failed to open Google Drive picker:", error);
        }
    };

    return (
        <button
            className={buttonClassName}
            onClick={handleOpen}
            disabled={isLoading}
        >
            {isLoading ? "Loading..." : buttonText}
        </button>
    );
};

export default GoogleDrivePicker;
